<template>
  <Teleport to="body">
    <Transition name="ts-lightbox-fade">
      <div
        v-if="current"
        class="ts-lightbox"
        role="dialog"
        aria-modal="true"
        @click.self="close"
        @touchstart.passive="onTouchStart"
        @touchend="onTouchEnd"
      >
        <div class="ts-lightbox__bar">
          <span v-if="images.length > 1" class="ts-lightbox__counter">{{ index + 1 }} / {{ images.length }}</span>
          <div class="ts-lightbox__actions">
            <button
              v-if="deletable"
              type="button"
              class="ts-lightbox__btn ts-lightbox__btn--danger"
              :title="$t('common.delete')"
              :aria-label="$t('common.delete')"
              @click="confirmDelete"
            >
              <i class="material-icons">delete</i>
            </button>
            <a
              class="ts-lightbox__btn"
              :href="current.src"
              target="_blank"
              rel="noopener"
              download
              :title="$t('common.download')"
              :aria-label="$t('common.download')"
            >
              <i class="material-icons">download</i>
            </a>
            <button
              ref="closeBtn"
              type="button"
              class="ts-lightbox__btn"
              :title="$t('common.close')"
              :aria-label="$t('common.close')"
              @click="close"
            >
              <i class="material-icons">close</i>
            </button>
          </div>
        </div>

        <div class="ts-lightbox__stage" @click.self="close">
          <button
            v-if="index > 0"
            type="button"
            class="ts-lightbox__btn ts-lightbox__nav ts-lightbox__nav--prev"
            :aria-label="$t('common.previous')"
            @click="prev"
          >
            <i class="material-icons">chevron_left</i>
          </button>

          <div v-if="!loaded" class="ts-lightbox__spinner"></div>
          <img
            :key="current.src"
            :src="current.src"
            :alt="current.alt || ''"
            class="ts-lightbox__image"
            :class="{ 'is-loaded': loaded }"
            @load="loaded = true"
            @error="loaded = true"
          />

          <button
            v-if="index < images.length - 1"
            type="button"
            class="ts-lightbox__btn ts-lightbox__nav ts-lightbox__nav--next"
            :aria-label="$t('common.next')"
            @click="next"
          >
            <i class="material-icons">chevron_right</i>
          </button>
        </div>

        <div v-if="images.length > 1" ref="strip" class="ts-lightbox__strip">
          <img
            v-for="(img, i) in images"
            :key="i"
            :src="img.thumbnail || img.src"
            alt=""
            loading="lazy"
            class="ts-lightbox__thumb"
            :class="{ 'is-active': i === index }"
            @click="go(i)"
          />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import swal from "sweetalert2";

// Fullscreen image viewer. Open by setting v-model:index to an image index, close by setting it to null.
export default {
  name: "ImageLightbox",
  props: {
    images: { type: Array, default: () => [] }, // [{ src, thumbnail?, alt? }]
    index: { type: Number, default: null },
    deletable: { type: Boolean, default: false },
  },
  emits: ["update:index", "delete"],
  data() {
    return { loaded: false, touchX: 0, touchY: 0, prevOverflow: "", confirming: false };
  },
  computed: {
    current() {
      return this.index !== null ? this.images[this.index] || null : null;
    },
  },
  watch: {
    current(val, old) {
      if (val && !old) this.onOpen();
      else if (!val && old) this.onClose();
      if (val && val.src !== old?.src) {
        this.loaded = false;
        this.$nextTick(() => {
          this.$refs.strip?.children[this.index]?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
        });
      }
    },
  },
  mounted() {
    if (this.current) this.onOpen();
  },
  beforeUnmount() {
    if (this.current) this.onClose();
  },
  methods: {
    onOpen() {
      this.prevOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", this.onKeydown);
      this.$nextTick(() => this.$refs.closeBtn?.focus());
    },
    onClose() {
      document.body.style.overflow = this.prevOverflow;
      document.removeEventListener("keydown", this.onKeydown);
    },
    close() { this.$emit("update:index", null); },
    go(i) { if (i >= 0 && i < this.images.length) this.$emit("update:index", i); },
    prev() { this.go(this.index - 1); },
    next() { this.go(this.index + 1); },
    async confirmDelete() {
      const index = this.index;
      this.confirming = true;
      const result = await swal.fire({
        title: this.$t("delete.areYouSure"),
        text: this.$t("delete.noRevert"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("delete.yesDeleteIt"),
        cancelButtonText: this.$t("delete.cancel"),
        customClass: { container: "ts-lightbox-swal", confirmButton: "btn btn-success btn-fill", cancelButton: "btn btn-danger btn-fill" },
        buttonsStyling: false,
      });
      this.confirming = false;
      if (result.isConfirmed) this.$emit("delete", index);
    },
    onKeydown(e) {
      // The confirm dialog handles its own keys
      if (this.confirming) return;
      if (e.key === "Escape") this.close();
      else if (e.key === "ArrowLeft") this.prev();
      else if (e.key === "ArrowRight") this.next();
    },
    onTouchStart(e) {
      this.touchX = e.changedTouches[0].clientX;
      this.touchY = e.changedTouches[0].clientY;
    },
    onTouchEnd(e) {
      const dx = e.changedTouches[0].clientX - this.touchX;
      const dy = e.changedTouches[0].clientY - this.touchY;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
        if (dx > 0) this.prev();
        else this.next();
      }
    },
  },
};
</script>

<style scoped>
.ts-lightbox {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  background: rgba(23, 29, 41, 0.92);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left);
}

.ts-lightbox__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  flex-shrink: 0;
}

.ts-lightbox__counter {
  padding: 4px 14px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.ts-lightbox__actions {
  display: flex;
  gap: 10px;
  margin-left: auto;
}

.ts-lightbox__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;
}

.ts-lightbox__btn:hover,
.ts-lightbox__btn:focus-visible {
  background: rgba(255, 255, 255, 0.24);
  color: #fff;
  outline: none;
}

.ts-lightbox__btn .material-icons {
  font-size: 22px;
}

.ts-lightbox__btn--danger:hover,
.ts-lightbox__btn--danger:focus-visible {
  background: linear-gradient(195deg, #ef5350, #e53935);
}

.ts-lightbox__stage {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 80px;
}

.ts-lightbox__image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 0.75rem;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
  opacity: 0;
  transition: opacity 0.25s ease;
  user-select: none;
}

.ts-lightbox__image.is-loaded {
  opacity: 1;
}

.ts-lightbox__nav {
  position: absolute;
  top: 50%;
  width: 52px;
  height: 52px;
  transform: translateY(-50%);
  z-index: 1;
}

.ts-lightbox__nav .material-icons {
  font-size: 32px;
}

.ts-lightbox__nav:hover {
  transform: translateY(-50%) scale(1.06);
}

.ts-lightbox__nav--prev { left: 16px; }
.ts-lightbox__nav--next { right: 16px; }

.ts-lightbox__spinner {
  position: absolute;
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ts-lightbox-spin 0.8s linear infinite;
}

@keyframes ts-lightbox-spin {
  to { transform: rotate(360deg); }
}

.ts-lightbox__strip {
  display: flex;
  gap: 8px;
  padding: 16px 20px;
  overflow-x: auto;
  flex-shrink: 0;
  margin: 0 auto;
  max-width: 100%;
  scrollbar-width: none;
}

.ts-lightbox__strip::-webkit-scrollbar {
  display: none;
}

.ts-lightbox__thumb {
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  object-fit: cover;
  border-radius: 0.5rem;
  border: 2px solid transparent;
  opacity: 0.5;
  cursor: pointer;
  transition: opacity 0.2s ease, border-color 0.2s ease;
}

.ts-lightbox__thumb:hover {
  opacity: 0.85;
}

.ts-lightbox__thumb.is-active {
  opacity: 1;
  border-color: #fff;
}

@media (max-width: 767px) {
  .ts-lightbox__bar { padding: 12px; }
  .ts-lightbox__stage { padding: 0 8px; }
  .ts-lightbox__nav {
    width: 40px;
    height: 40px;
    background: rgba(0, 0, 0, 0.35);
  }
  .ts-lightbox__nav--prev { left: 12px; }
  .ts-lightbox__nav--next { right: 12px; }
  .ts-lightbox__strip { padding: 12px; }
  .ts-lightbox__thumb { width: 48px; height: 48px; }
}

.ts-lightbox-fade-enter-active,
.ts-lightbox-fade-leave-active {
  transition: opacity 0.2s ease;
}

.ts-lightbox-fade-enter-from,
.ts-lightbox-fade-leave-to {
  opacity: 0;
}
</style>

<style>
/* SweetAlert mounts on <body> at z-index 1060; lift the delete confirmation above the lightbox */
.swal2-container.ts-lightbox-swal {
  z-index: 10001;
}
</style>
